import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    LayoutDashboard, 
    ClipboardList, 
    Users, 
    LogOut,
    CalendarDays,
    UserCircle,
    Clock,
    Palmtree,
    UserPlus,
    Shield,
    MapPin,
    Settings,
    Calculator
} from 'lucide-react';
import { Button } from './ui/button';

export const Sidebar = () => {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const adminLinks = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/my-orders', icon: ClipboardList, label: 'Meine Aufträge' },
        { to: '/orders', icon: ClipboardList, label: 'Alle Aufträge' },
        { to: '/customers', icon: Users, label: 'Kunden' },
        { to: '/employees', icon: UserPlus, label: 'Mitarbeiter' },
        { to: '/live-tracking', icon: MapPin, label: 'Live-Tracking' },
        { to: '/time-management', icon: Clock, label: 'Arbeitszeiten' },
        { to: '/payroll', icon: Calculator, label: 'Stundenabrechnung' },
        { to: '/admins', icon: Shield, label: 'Admins' },
        { to: '/vacation', icon: Palmtree, label: 'Urlaubsverwaltung' },
        { to: '/settings', icon: Settings, label: 'Einstellungen' },
    ];

    const employeeLinks = [
        { to: '/my-orders', icon: ClipboardList, label: 'Meine Aufträge' },
        { to: '/calendar', icon: CalendarDays, label: 'Kalender' },
        { to: '/time-tracking', icon: Clock, label: 'Zeiterfassung' },
        { to: '/vacation', icon: Palmtree, label: 'Urlaub' },
        { to: '/profile', icon: UserCircle, label: 'Profil' },
    ];

    const links = isAdmin() ? adminLinks : employeeLinks;

    return (
        <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r flex flex-col hide-mobile" data-testid="sidebar">
            {/* Logo */}
            <div className="p-6 border-b">
                <h1 className="text-xl font-bold tracking-tight">Fa. Thut Manager</h1>
                <p className="text-xs text-muted-foreground mt-1">Auftragsverwaltung</p>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
                {links.map((link) => (
                    <NavLink
                        key={link.to}
                        to={link.to}
                        className={({ isActive }) =>
                            `sidebar-link ${isActive ? 'active' : ''}`
                        }
                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                        <link.icon className="h-5 w-5" strokeWidth={1.5} />
                        <span>{link.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* User section */}
            <div className="p-4 border-t">
                <div className="flex items-center gap-3 mb-4 px-2">
                    <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                        {user?.name?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.role === 'ADMIN' ? 'Administrator' : 'Mitarbeiter'}</p>
                    </div>
                </div>
                <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    Abmelden
                </Button>
            </div>
        </aside>
    );
};

export default Sidebar;
